'use client';

import dynamic from 'next/dynamic';
import { useReducedMotion } from 'framer-motion';

import { OnboardingOverlay } from './OnboardingOverlay';
import { CREATOR_STEPS, type SceneId } from './copy';
import { useCreatorOnboarding } from './use-onboarding-state';

const TourStage2D = dynamic(
  () => import('./tour-stage/TourStage2D').then((m) => m.TourStage2D),
  { ssr: false, loading: () => null },
);

interface CreatorOnboardingProps {
  /** Pre-built flow controller — see PlayerOnboarding for the rationale. */
  flow: ReturnType<typeof useCreatorOnboarding>;
}

/**
 * 5-step creator intro for `/create`.
 *
 * Shares the CSS-driven `TourStage2D` with the player flow for
 * consistent alignment and motion. Gated by `CREATOR_KEY` in
 * localStorage; the wizard remains interactive underneath so an
 * experienced creator can ESC out and start filling fields immediately.
 */
export function CreatorOnboarding({ flow }: CreatorOnboardingProps) {
  const reduceMotion = useReducedMotion();

  return (
    <OnboardingOverlay
      open={flow.open}
      steps={CREATOR_STEPS}
      eyebrow="Creator intro"
      renderStage={(scene: SceneId) => (
        <TourStage2D scene={scene} reducedMotion={!!reduceMotion} />
      )}
      onSkip={flow.dismiss}
      onComplete={flow.dismiss}
    />
  );
}
